import type { ReactNode } from 'react';
import { DeniedState, EmptyState, ErrorState, LoadingSkeleton } from '@/shared/feedback';
import { styles } from '@/shared/app-list-layout/styles';
import { EngineTable } from '@/shared/app-list-layout/parts/engine-adapter';
import { Header } from '@/shared/app-list-layout/parts/header';
import type { AppListLayoutProps } from '@/shared/app-list-layout/types';

/**
 * Re-exported so a list page needs one import path for its data and query
 * types as well as the layout itself.
 */
export type {
  DataTableColumnFilter,
  DataTableParams,
  DataTableSort,
  DataTableView,
} from '@/shared/ui/data-table';

/**
 * The list surface every list page shares: the four states, the saved-view tabs,
 * the search box, the column filters, the pager and the table/cards toggle.
 *
 * A page supplies its columns, its read and its words; this owns everything else,
 * so a dozen pages cannot drift apart.
 */
export { AppList } from '@/shared/app-list-layout/app-list';
export type { AppListProps } from '@/shared/app-list-layout/app-list';

/** The local query engine and the `onFetch` handler the shared surface uses. */
export { applyQuery, countBy } from '@/shared/app-list-layout/query';
export type { SearchFields } from '@/shared/app-list-layout/query';
export { useListFetch } from '@/shared/app-list-layout/use-list-fetch';
export type {
  ListFetchHandler,
  ListFetchSink,
  ListPhase,
} from '@/shared/app-list-layout/use-list-fetch';

/**
 * State precedence, identical to the detail layout: denial outranks everything,
 * because a "not found" or an empty state shown to a denied user leaks that the
 * record collection exists.
 *
 * Every non-ready state is a layout surface, not a table state: a denied or
 * failed read has no table to draw, and the empty state carries the module's
 * own action (which a row of no data cannot).
 */
function nonReadySurface(props: AppListLayoutProps<unknown>): ReactNode | null {
  if (props.isDenied) return <DeniedState />;
  if (props.error) {
    return <ErrorState title="Could not load" message={props.error.message} />;
  }
  if (props.isLoading) return <LoadingSkeleton />;
  if (props.isEmpty) {
    return (
      <EmptyState
        title={props.emptyTitle ?? 'Nothing here yet'}
        {...(props.emptyDescription ? { description: props.emptyDescription } : {})}
        {...(props.emptyAction ? { action: props.emptyAction } : {})}
      />
    );
  }
  return null;
}

/**
 * The reusable list layout.
 *
 * The layout owns the page frame — header, notice banner, filter slot and the
 * four state surfaces — and delegates the table itself to the ONE shared table
 * engine. The module supplies columns as typed descriptors; the engine supplies
 * every row, header, selection control and pager, which is what stops 82
 * modules each writing their own.
 */
export function AppListLayout<T>(props: AppListLayoutProps<T>): ReactNode {
  const surface = nonReadySurface(props as AppListLayoutProps<unknown>);

  return (
    <section className={styles.page} aria-label={props.title} aria-busy={props.isLoading || undefined}>
      <Header {...props} />

      {props.banner ? <div className={styles.banner}>{props.banner}</div> : null}
      {props.filters ? <div className={styles.filters}>{props.filters}</div> : null}

      {surface ? (
        <div className={styles.surface}>{surface}</div>
      ) : (
        <EngineTable props={props} />
      )}
    </section>
  );
}
